import React, { useEffect, useRef, useState } from 'react';
import { listSupportChatsForAdmin, sendAdminMessage, sendPlanCard } from '../lib/supportChats.js';
import { loadSubscriptionConfig } from '../lib/subscriptionConfig.js';
import { subscribeToLatestMessages } from '../../../lib/db.js';
import './SupportInboxPage.css';

// ============================================================
// SupportInboxPage — Session 5a (Phase 2).
//
// First admin surface with a LIVE Firestore subscription. Reads
// /chats where chatType == 'support' newest-first, and when a chat
// is selected, subscribes to its /messages subcollection live so new
// business-side messages appear without refresh.
//
// LAYOUT
// ------
// Master-detail on one page. Left rail = chat list (paginated). Right
// pane = selected chat (message thread + reply input). No chat
// selected → empty-state prompt on the right.
//
// COST
// ----
// Inbox mount:         25 reads
// Load more:           25 per click
// Select chat:         1 message-history read (via subscribeToLatestMessages
//                      pageSize default) + live subscription for new msgs
// Send admin reply:    2 writes (message add + chat setDoc merge)
// Post-send:           0 reads (message appears via the live subscription)
// Switch chat:         cleans up previous subscription, opens new one
//
// LIVE SUBSCRIPTIONS
// ------------------
// Cleaned up on chat switch AND on unmount via the useEffect return.
// A leaked subscription would keep pulling reads after the admin
// navigated away — an unacceptable cost regression against Phase 2's
// bounded-reads discipline.
// ============================================================

// Format a Firestore Timestamp / Date / ISO string as a relative or
// absolute time string. Used for the chat list preview and message
// timestamps.
function fmtTimestamp(ts, style = 'short') {
  if (!ts) return '';
  let d;
  if (typeof ts?.toDate === 'function') d = ts.toDate();
  else if (ts instanceof Date) d = ts;
  else if (typeof ts?.seconds === 'number') d = new Date(ts.seconds * 1000);
  else if (typeof ts === 'string') d = new Date(ts);
  else return '';
  if (Number.isNaN(d.getTime())) return '';

  if (style === 'time') {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  // 'short' style: today → time; other → dd MMM
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// Extract the business-owner UID from a support chat doc. The chat
// participants are [ownerUid, aangansupportUid]; whichever is NOT
// aangansupport is the owner.
const AANGAN_SUPPORT_UID = 'RS0iaHZppgQeaNT4bGt7KWRbWyZ2';
function ownerUidOf(chat) {
  const p = chat?.participants || [];
  return p.find((uid) => uid !== AANGAN_SUPPORT_UID) || '';
}

export default function SupportInboxPage({ authUser, awaitingChatIds = [] }) {
  // Session 5d.2 — which chats are waiting on a reply. Supplied live by
  // AdminShell (one subscription feeds both the sidebar badge and these
  // row markers, so a count of 2 always means exactly 2 dotted rows).
  // The local `chats` list below is a one-shot getDocs page and would go
  // stale the moment a merchant wrote, so it must NOT be the source here.
  const awaitingSet = React.useMemo(
    () => new Set(awaitingChatIds),
    [awaitingChatIds],
  );
  // ==========================================================
  // Inbox (left rail) state
  // ==========================================================
  const [chats, setChats] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [selectedChatId, setSelectedChatId] = useState(null);

  // ==========================================================
  // Message thread (right pane) state
  // ==========================================================
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // ==========================================================
  // Session 5c — plan card composer state
  // ==========================================================
  // Tiers load once on mount (1 read, or 0 if the config doc is absent
  // and FALLBACK_TIERS kicks in). Shared across every chat the admin
  // opens in this session — no re-fetch per selection.
  const [tiers, setTiers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sendingTierId, setSendingTierId] = useState(null);

  const scrollRef = useRef(null);

  // Load Aangan Connect tiers once. Failure is non-fatal: the picker
  // simply won't render and the admin can still type a normal reply.
  useEffect(() => {
    let cancelled = false;
    loadSubscriptionConfig()
      .then((res) => {
        if (!cancelled) setTiers(res.tiers || []);
      })
      .catch((err) => {
        console.error('[SupportInboxPage] tier config load failed:', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch initial page of support chats.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listSupportChatsForAdmin({ cursor: null })
      .then((res) => {
        if (cancelled) return;
        setChats(res.chats);
        setLastDoc(res.lastDoc);
        setHasMore(res.hasMore);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[SupportInboxPage] initial load failed:', err);
        setError(err?.message || 'Failed to load support chats');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Live subscription to selected chat's messages. Re-runs whenever
  // selectedChatId changes; cleanup unsubscribes the previous chat
  // before opening the new one (or on unmount).
  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      return;
    }

    setMessagesLoading(true);
    setMessagesError(null);
    setMessages([]);
    // Session 5c — collapse the plan picker when switching conversations
    // so a card intended for one merchant can't be fired at the next.
    setPickerOpen(false);

    // pageSize = 50 shows recent history without becoming expensive.
    // Earlier messages accessible via a "Load older" button — deferred
    // to Session 5b polish.
    const unsub = subscribeToLatestMessages(
      selectedChatId,
      50,
      (msgs) => {
        setMessages(msgs);
        setMessagesLoading(false);
      },
    );

    return () => {
      try { unsub?.(); } catch (_) { /* ignore */ }
    };
  }, [selectedChatId]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, selectedChatId]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore || !lastDoc) return;
    setLoadingMore(true);
    try {
      const res = await listSupportChatsForAdmin({ cursor: lastDoc });
      setChats((prev) => [...prev, ...res.chats]);
      setLastDoc(res.lastDoc);
      setHasMore(res.hasMore);
    } catch (err) {
      console.error('[SupportInboxPage] load-more failed:', err);
      setError(err?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSend = async () => {
    if (!selectedChatId || sending) return;
    const trimmed = replyText.trim();
    if (!trimmed) return;

    setSending(true);
    setSendError(null);
    try {
      await sendAdminMessage({
        chatId: selectedChatId,
        adminUid: authUser?.uid,
        text: trimmed,
      });
      setReplyText('');
      // Live subscription will append the message; no state update here.
    } catch (err) {
      console.error('[SupportInboxPage] send failed:', err);
      setSendError(err?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Enter sends; Shift+Enter adds a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Session 5c — send a structured Aangan Connect plan card.
  //
  // Replaces the old flow of typing "₹199 gets you 1 month..." by hand.
  // The merchant sees a rendered card with a one-tap CTA; their tap
  // arrives back here as a plain text reply stating which plan they
  // want. Admin then activates via the Businesses page ActivationModal.
  const handleSendPlanCard = async (tier) => {
    if (!selectedChatId || sendingTierId) return;
    setSendingTierId(tier.id);
    setSendError(null);
    try {
      await sendPlanCard({
        chatId: selectedChatId,
        adminUid: authUser?.uid,
        tier,
      });
      setPickerOpen(false);
      // Live subscription appends the card; no state update here.
    } catch (err) {
      console.error('[SupportInboxPage] plan card send failed:', err);
      setSendError(err?.message || 'Could not send plan card');
    } finally {
      setSendingTierId(null);
    }
  };

  const selectedChat = chats.find((c) => c.id === selectedChatId);

  // Session A — the Support Inbox now also receives chats opened from V1's
  // Help & Support screen by users who have no business. Offering them an
  // Aangan Connect plan card would be nonsense, so the picker is hidden.
  //
  // Legacy chats predate the openedFrom field; they were all opened by
  // business owners, so absence of the field means 'merchant'. Only an
  // explicit 'v1' suppresses the picker.
  const isV1MemberChat = selectedChat?.openedFrom === 'v1';

  // Session 5d — "Owner" is wrong for a V1 member who has no business.
  const counterpartyLabel = isV1MemberChat ? 'Member' : 'Owner';

  return (
    <div className="sa-support-layout">
      {/* ==================================================
          Left rail — chat list
          ================================================== */}
      <div className="sa-support-list">
        <div className="sa-support-list-header">
          <div className="sa-support-list-title">Support conversations</div>
          <div className="sa-support-list-count">
            {loading ? '…' : `${chats.length} chat${chats.length === 1 ? '' : 's'}`}
          </div>
        </div>

        {error && (
          <div className="sa-error-banner">
            <b>Couldn't load support inbox.</b> {error}
          </div>
        )}

        <div className="sa-support-list-body">
          {loading && <div className="sa-support-list-empty">Loading…</div>}

          {!loading && chats.length === 0 && !error && (
            <div className="sa-support-list-empty">
              No support conversations yet. Businesses can start one from Manage → Chat with Aangan support.
            </div>
          )}

          {!loading && chats.map((chat) => {
            const ownerUid = ownerUidOf(chat);
            const displayName = chat.ownerName || ownerUid || '—';
            const isActive = chat.id === selectedChatId;
            const isAwaiting = awaitingSet.has(chat.id);
            const preview = chat.lastMessage || <span className="sa-support-list-empty-preview">No messages yet</span>;

            return (
              <div
                key={chat.id}
                className={`sa-support-list-item ${isActive ? 'active' : ''} ${isAwaiting ? 'awaiting' : ''}`}
                onClick={() => setSelectedChatId(chat.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedChatId(chat.id);
                  }
                }}
              >
                <div className="sa-support-list-item-row1">
                  {/* Session 5a polish: prefer denormalized ownerName
                      when present. Falls back to raw UID for chats
                      created before the field existed. UID stays
                      accessible via the tooltip for admin debugging. */}
                  <div className="sa-support-list-item-owner" title={ownerUid}>
                    {isAwaiting && (
                      <span
                        className="sa-support-awaiting-dot"
                        aria-label="Awaiting your reply"
                        title="Awaiting your reply"
                      />
                    )}
                    {displayName}
                  </div>
                  <div className="sa-support-list-item-time">
                    {fmtTimestamp(chat.lastMessageAt)}
                  </div>
                </div>
                <div className="sa-support-list-item-preview">{preview}</div>
              </div>
            );
          })}

          {!loading && hasMore && (
            <div className="sa-support-list-loadmore">
              <button className="sa-btn" onClick={handleLoadMore} disabled={loadingMore} type="button">
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ==================================================
          Right pane — message thread
          ================================================== */}
      <div className="sa-support-thread">
        {!selectedChatId && (
          <div className="sa-support-thread-empty">
            <div className="sa-support-thread-empty-icon">💬</div>
            <div className="sa-support-thread-empty-text">
              Select a conversation on the left to view messages.
            </div>
          </div>
        )}

        {selectedChatId && (
          <>
            <div className="sa-support-thread-header">
              <div>
                <div className="sa-support-thread-title">
                  {selectedChat?.ownerName || ownerUidOf(selectedChat) || 'Business owner'}
                </div>
                <div className="sa-support-thread-sub">
                  Owner {ownerUidOf(selectedChat)} · Chat ID {selectedChatId}
                </div>
              </div>
            </div>

            <div className="sa-support-thread-messages" ref={scrollRef}>
              {messagesLoading && (
                <div className="sa-support-thread-empty-line">Loading messages…</div>
              )}

              {messagesError && (
                <div className="sa-error-banner">
                  <b>Couldn't load messages.</b> {messagesError}
                </div>
              )}

              {!messagesLoading && messages.length === 0 && !messagesError && (
                <div className="sa-support-thread-empty-line">
                  No messages yet. Say hello to the business owner.
                </div>
              )}

              {!messagesLoading && messages.map((msg) => {
                // Same senderRole-first display logic as SupportChatModal.
                // Admin-side: 'admin' bubbles go on the RIGHT (labeled "You"
                // — the currently signed-in admin), owner messages go on
                // the LEFT (labeled "Owner"). Fallback for pre-senderRole
                // messages: fromId != ownerUid means an admin authored it.
                const ownerUid = ownerUidOf(selectedChat);
                const isAdminMsg =
                  msg.senderRole === 'support'
                  || (msg.fromId && msg.fromId !== ownerUid);

                // Session 5c — show a compact echo of the plan card the
                // admin sent, so the thread reads coherently rather than
                // showing only the fallback text line.
                const isPlanCard = msg.type === 'plan-card' && isAdminMsg;

                return (
                  <div
                    key={msg.id}
                    className={`sa-support-msg ${isAdminMsg ? 'sa-support-msg-admin' : 'sa-support-msg-owner'}`}
                  >
                    {isPlanCard && msg.planCard ? (
                      <div className="sa-support-plan-chip">
                        <div className="sa-support-plan-chip-label">Aangan Connect plan sent</div>
                        <div className="sa-support-plan-chip-value">
                          ₹{msg.planCard.amount} · {msg.planCard.label}
                        </div>
                      </div>
                    ) : (
                      <div className="sa-support-msg-bubble">
                        {msg.text || (msg.imageUrl ? '📷 Photo' : '')}
                      </div>
                    )}
                    <div className="sa-support-msg-meta">
                      {isAdminMsg ? 'You' : counterpartyLabel} · {fmtTimestamp(msg.sentAt, 'time')}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Session 5c — plan card picker. Collapsed by default so the
                composer stays a plain reply box; expands into the tier
                buttons on demand. Hidden if tiers failed to load, and
                hidden for V1-member chats (Session A) since a member has
                no business to activate. */}
            {tiers.length > 0 && !isV1MemberChat && (
              <div className="sa-support-plan-bar">
                <button
                  type="button"
                  className="sa-support-plan-toggle"
                  onClick={() => setPickerOpen((v) => !v)}
                  disabled={sending || !!sendingTierId}
                >
                  <span>Send Aangan Connect plan</span>
                  <span className="sa-support-plan-caret">{pickerOpen ? '−' : '+'}</span>
                </button>

                {pickerOpen && (
                  <div className="sa-support-plan-options">
                    {tiers.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className="sa-support-plan-option"
                        onClick={() => handleSendPlanCard(t)}
                        disabled={!!sendingTierId}
                      >
                        <span className="sa-support-plan-option-amount">₹{t.amount}</span>
                        <span className="sa-support-plan-option-label">
                          {sendingTierId === t.id ? 'Sending…' : t.label}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="sa-support-thread-composer">
              {sendError && (
                <div className="sa-support-thread-send-error">{sendError}</div>
              )}
              <textarea
                className="sa-support-thread-input"
                placeholder="Reply as Aangan Support…"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                disabled={sending}
              />
              <button
                className="sa-btn sa-btn-primary"
                onClick={handleSend}
                disabled={sending || !replyText.trim()}
                type="button"
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
